import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import UserTableHeader from './components/UserTableHeader';
import UserTableRow from './components/UserTableRow';
import FilterBar from './components/FilterBar';
import BulkActionsBar from './components/BulkActionsBar';
import CreateUserModal from './components/CreateUserModal';
import EditUserModal from './components/EditUserModal';
import ResetPasswordModal from './components/ResetPasswordModal';
import DeleteUserModal from './components/DeleteUserModal';
import UserRolesSection from '../multi-role-user-profiles/components/UserRolesSection';
import RoleAssignmentsSection from '../multi-role-user-profiles/components/RoleAssignmentsSection';
import PermissionsMatrix from '../multi-role-user-profiles/components/PermissionsMatrix';
import GeographicAccessSection from '../multi-role-user-profiles/components/GeographicAccessSection';
import AssignRoleModal from '../multi-role-user-profiles/components/AssignRoleModal';
import { userRoleService } from '../../services/userRoleService';
import { getTranslation } from '../../utils/translations';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { resolveAdminIdentityState, ADMIN_MAIN_TABS } from './adminIdentityTabUtils';
import {
  getPrimaryRoleType,
  getUserActiveRoleTypes,
  userMatchesRoleFilter,
  userMatchesSearch,
} from './userFilterUtils';
import { syncUserRoleTypes } from './syncUserRoles';

const AdminUsersManagement = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [searchParams, setSearchParams] = useSearchParams();
  const identityFetchedRef = useRef(false);

  const spaKey = searchParams?.toString();
  const { mainTab, identityView, redirect } = useMemo(
    () => resolveAdminIdentityState(searchParams),
    [spaKey, searchParams]
  );

  useEffect(() => {
    if (redirect) {
      setSearchParams(redirect, { replace: true });
    }
  }, [redirect, setSearchParams]);

  const [currentLanguage] = useState(() => localStorage.getItem('language') || 'FR');
  const t = getTranslation(currentLanguage);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    role: 'all',
    status: 'all',
  });

  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityRolesAll, setIdentityRolesAll] = useState([]);
  const [identityAssignments, setIdentityAssignments] = useState([]);
  const [identityPermissions, setIdentityPermissions] = useState([]);
  const [identityUsersRaw, setIdentityUsersRaw] = useState([]);
  const [identityCountries, setIdentityCountries] = useState([]);

  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);

  useEffect(() => {
    localStorage.setItem('language', currentLanguage);
  }, [currentLanguage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const profileFilters = { search: filters.search };
      if (filters.status !== 'all') {
        profileFilters.status = filters.status;
      }
      const profiles = await userRoleService?.getAllUserProfiles(profileFilters);

      const usersWithRoles = await Promise.all(
        profiles?.map(async (profile) => {
          const assignments = await userRoleService?.getUserRoleAssignments(profile?.id);
          const userStub = { roleAssignments: assignments };
          const primaryRole = getPrimaryRoleType(userStub);

          return {
            id: profile?.id,
            name: profile?.fullName,
            email: profile?.email,
            initials: profile?.fullName?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase(),
            role: primaryRole,
            status: profile?.status,
            lastLogin: profile?.lastLoginAt,
            createdAt: profile?.createdAt,
            roleAssignments: assignments,
          };
        })
      );

      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mainTab !== 'users') return;
    fetchUsers();
  }, [mainTab, filters.search, filters.status]);

  const refreshIdentityBundle = async ({ silent = false } = {}) => {
    try {
      if (!silent) setIdentityLoading(true);

      const [rolesData, assignmentsData, permissionsData, usersData] = await Promise.all([
        userRoleService?.getAllRoles(),
        userRoleService?.getAllRoleAssignments(),
        userRoleService?.getAllPermissions(),
        userRoleService?.getAllUserProfiles(),
      ]);

      setIdentityRolesAll(rolesData || []);
      setIdentityAssignments(assignmentsData || []);
      setIdentityPermissions(permissionsData || []);
      setIdentityUsersRaw(usersData || []);

      const { data: countriesData } = await supabase
        .from('countries')
        .select('*')
        .eq('is_active', true);
      setIdentityCountries(countriesData || []);
    } catch (error) {
      console.error('Error loading roles & permissions:', error);
    } finally {
      if (!silent) setIdentityLoading(false);
    }
  };

  useEffect(() => {
    if (mainTab === 'users') return;
    if (identityFetchedRef.current) return;
    identityFetchedRef.current = true;
    refreshIdentityBundle({ silent: false });
  }, [mainTab]);

  const handleIdentityRefreshAndUsers = async () => {
    await refreshIdentityBundle({ silent: true });
    await fetchUsers();
  };

  const identityRolesAssignable = useMemo(
    () => (identityRolesAll || []).filter((r) => r?.roleType !== 'operators'),
    [identityRolesAll]
  );

  const selectMainTab = (id) => {
    if (!ADMIN_MAIN_TABS.includes(id)) return;
    if (id === 'users') {
      setSearchParams({}, { replace: true });
      return;
    }
    if (id === 'reference') {
      setSearchParams({ tab: 'reference', view: 'roles' }, { replace: true });
      return;
    }
    setSearchParams({ tab: 'assignments', view: 'users' }, { replace: true });
  };

  const setIdentitySubView = (view) => {
    if (mainTab === 'reference') {
      setSearchParams({ tab: 'reference', view }, { replace: true });
    } else if (mainTab === 'assignments') {
      setSearchParams({ tab: 'assignments', view }, { replace: true });
    }
  };

  const ai = t?.adminIdentity || {};

  const mainTabsUi = [
    { id: 'users', label: t?.users || 'Users', icon: 'UserCog' },
    { id: 'reference', label: ai.referenceAndPermissions || 'Reference', icon: 'Layers' },
    { id: 'assignments', label: ai.assignmentsAndAccess || 'Assignments', icon: 'UserCheck' },
  ];

  const breadcrumbItems = [
    { label: t?.dashboard, path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Administration', path: '', icon: 'Settings' },
    { label: t?.usersAndAccess || t?.users, path: '/admin-users-management' },
  ];

  const filterUsers = () => {
    let filtered = [...users];

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((user) => userMatchesSearch(user, searchLower));
    }

    if (filters?.role !== 'all') {
      filtered = filtered.filter((user) => userMatchesRoleFilter(user, filters.role));
    }

    if (filters?.status !== 'all') {
      filtered = filtered.filter((user) => user?.status === filters.status);
    }

    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];

      if (sortBy === 'lastLogin' || sortBy === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const filteredUsers = filterUsers();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      role: 'all',
      status: 'all',
    });
  };

  const handleSort = (key, order) => {
    setSortBy(key);
    setSortOrder(order);
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers((prev) => [...prev, userId]);
    } else {
      setSelectedUsers((prev) => prev?.filter((id) => id !== userId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(filteredUsers?.map((user) => user?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  const handleCreateUser = async (userData) => {
    try {
      await userRoleService?.createUser({
        fullName: userData?.name,
        email: userData?.email,
        password: userData?.password,
        countryId: userData?.countryId,
        caseNumber: userData?.caseNumber || null,
        currentCaseNumber: userData?.currentCaseNumber || null,
      });

      const newUserProfile = await userRoleService?.getAllUserProfiles({ search: userData?.email });
      const userProfile = newUserProfile?.[0];

      if (!userProfile) {
        throw new Error('Profil utilisateur non trouvé après création');
      }

      const rolesToAssign =
        userData?.roles?.length > 0
          ? userData.roles
          : userData?.role
            ? [userData.role]
            : [];

      if (rolesToAssign.length > 0) {
        await syncUserRoleTypes(userProfile.id, rolesToAssign, {
          countryId: userData?.countryId || null,
        });
      }

      if (userData?.packageId && rolesToAssign.includes('dentist_client')) {
        const { error: packageError } = await supabase
          .from('user_profiles')
          .update({ package_id: userData?.packageId })
          .eq('id', userProfile?.id);

        if (packageError) {
          console.error("Erreur lors de l'attribution du package:", packageError);
          toast.warning("Utilisateur créé mais le package n'a pas pu être attribué");
        }
      }

      await fetchUsers();
      if (identityFetchedRef.current) {
        await refreshIdentityBundle({ silent: true });
      }
      toast.success('Utilisateur créé avec succès', {
        description: userData?.packageId ? 'Package attribué avec succès' : undefined,
      });
    } catch (error) {
      console.error('Error creating user:', error);
      const errorMessage = error?.message || 'Une erreur inconnue est survenue';
      toast.error("Échec de création de l'utilisateur", {
        description: errorMessage,
      });
      throw error;
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      await userRoleService?.updateUserProfile(userId, {
        fullName: userData?.name,
        email: userData?.email,
        status: userData?.status,
      });

      if (Array.isArray(userData?.roles)) {
        await syncUserRoleTypes(userId, userData.roles);
      }

      await fetchUsers();
      if (identityFetchedRef.current) {
        await refreshIdentityBundle({ silent: true });
      }
      toast.success('Utilisateur mis à jour');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erreur lors de la mise à jour', { description: error?.message });
      throw error;
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (userId, isPermanent = false) => {
    try {
      if (isPermanent) {
        await userRoleService?.permanentDeleteUser(userId);
      } else {
        await userRoleService?.deleteUser(userId);
      }

      await fetchUsers();
      if (identityFetchedRef.current) {
        await refreshIdentityBundle({ silent: true });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user: ' + error?.message);
    }
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
  };

  const handleToggleStatus = async (user) => {
    try {
      const newStatus = user?.status === 'active' ? 'inactive' : 'active';
      await userRoleService?.updateUserProfile(user?.id, { status: newStatus });
      await fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
    }
  };

  const handleRolesChange = async (userId, roleTypes) => {
    try {
      await syncUserRoleTypes(userId, roleTypes);
      await fetchUsers();
      if (identityFetchedRef.current) {
        await refreshIdentityBundle({ silent: true });
      }
    } catch (error) {
      console.error('Error syncing roles:', error);
      toast.error('Erreur lors de la mise à jour des rôles', { description: error?.message });
      throw error;
    }
  };

  const handleBulkAction = async (action) => {
    try {
      switch (action) {
        case 'activate':
          await userRoleService?.bulkUpdateUserStatus(selectedUsers, 'active');
          break;
        case 'deactivate':
          await userRoleService?.bulkUpdateUserStatus(selectedUsers, 'inactive');
          break;
        case 'delete':
          if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedUsers?.length} utilisateur(s)?`)) {
            await userRoleService?.permanentDeleteUser(selectedUsers);
          }
          break;
        default:
          break;
      }

      setSelectedUsers([]);
      await fetchUsers();
      if (identityFetchedRef.current) {
        await refreshIdentityBundle({ silent: true });
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      alert('Error performing bulk action: ' + error?.message);
    }
  };

  const handleAssignIdentityRole = async (assignmentData) => {
    try {
      await userRoleService?.assignRole(assignmentData);
      await handleIdentityRefreshAndUsers();
      setShowAssignRoleModal(false);
      toast.success(
        currentLanguage === 'FR' ? 'Rôle affecté' : 'Role assigned',
        { description: currentLanguage === 'FR' ? 'Les données ont été mises à jour.' : 'Data has been updated.' }
      );
    } catch (error) {
      console.error('Error assigning role:', error);
      toast.error(currentLanguage === 'FR' ? "Échec de l'affectation" : 'Assignment failed', {
        description: error?.message,
      });
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {t?.usersAndAccess || t?.users}
              </h1>
              <p className="text-muted-foreground mt-2">
                {t?.usersAndAccessSubtitle ||
                  'Gérer les comptes, les rôles et les permissions'}
              </p>
            </div>
            {mainTab === 'users' && (
              <div className="flex items-center gap-3">
                <Button
                  variant="default"
                  onClick={() => setShowCreateModal(true)}
                  iconName="UserPlus"
                  iconPosition="left"
                  iconSize={18}
                >
                  Ajouter un utilisateur
                </Button>
              </div>
            )}
            {mainTab === 'assignments' && identityView === 'users' && (
              <Button
                variant="default"
                onClick={() => setShowAssignRoleModal(true)}
                iconName="UserPlus"
                iconPosition="left"
                iconSize={18}
              >
                {t?.roles?.assignRole}
              </Button>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg p-1 flex flex-wrap gap-1 mb-6">
            {mainTabsUi.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => selectMainTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 ${
                  mainTab === tab.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon} size={18} />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>

          {mainTab === 'reference' && !identityLoading && (
            <>
              <p className="text-sm text-muted-foreground mb-3 max-w-3xl">{ai.referenceIntro}</p>
              <div className="flex flex-wrap gap-1 mb-6">
                <button
                  type="button"
                  onClick={() => setIdentitySubView('roles')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                    identityView === 'roles'
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Users" size={16} />
                  {ai.viewRolesCatalog || t?.roles?.userRoles}
                </button>
                <button
                  type="button"
                  onClick={() => setIdentitySubView('permissions')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                    identityView === 'permissions'
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name="Shield" size={16} />
                  {ai.viewPermissionsMatrix || t?.roles?.permissions}
                </button>
              </div>
            </>
          )}

          {mainTab === 'assignments' && !identityLoading && (
            <div className="flex flex-wrap gap-1 mb-6">
              <button
                type="button"
                onClick={() => setIdentitySubView('users')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                  identityView === 'users'
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Users" size={16} />
                {ai.viewByUser}
              </button>
              <button
                type="button"
                onClick={() => setIdentitySubView('geo')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                  identityView === 'geo'
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border text-muted-foreground hover:bg-muted'
                }`}
              >
                <Icon name="MapPin" size={16} />
                {ai.viewByCountry}
              </button>
            </div>
          )}

          {mainTab === 'users' && (
            <>
              <FilterBar
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                resultCount={filteredUsers?.length}
              />

              <BulkActionsBar
                selectedCount={selectedUsers?.length}
                onBulkAction={handleBulkAction}
                onClearSelection={handleClearSelection}
              />

              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <UserTableHeader
                      allSelected={
                        selectedUsers?.length === filteredUsers?.length && filteredUsers?.length > 0
                      }
                      onSelectAll={handleSelectAll}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center">
                            <div className="flex items-center justify-center gap-2 text-muted-foreground">
                              <Icon name="Loader" size={20} className="animate-spin" />
                              <span className="text-sm">Loading users...</span>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredUsers?.map((user) => (
                          <UserTableRow
                            key={user?.id}
                            user={user}
                            onEdit={handleEditUser}
                            onResetPassword={handleResetPassword}
                            onToggleStatus={handleToggleStatus}
                            onRolesChange={handleRolesChange}
                            onDelete={handleDeleteUser}
                            isSelected={selectedUsers?.includes(user?.id)}
                            onSelect={handleSelectUser}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {!loading && filteredUsers?.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 px-4">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                      <Icon name="Users" size={32} className="text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Aucun utilisateur trouvé</h3>
                    <p className="text-sm text-muted-foreground text-center max-w-md">
                      Aucun utilisateur ne correspond à vos filtres actuels. Essayez de modifier vos critères de
                      recherche ou créez un nouvel utilisateur.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {mainTab !== 'users' && (
            <div className="animate-fade-in">
              {identityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
              ) : (
                <>
                  {mainTab === 'reference' && identityView === 'roles' && (
                    <UserRolesSection
                      roles={identityRolesAll}
                      language={currentLanguage}
                      onRefresh={handleIdentityRefreshAndUsers}
                    />
                  )}
                  {mainTab === 'reference' && identityView === 'permissions' && (
                    <PermissionsMatrix
                      roles={identityRolesAll}
                      permissions={identityPermissions}
                      language={currentLanguage}
                    />
                  )}
                  {mainTab === 'assignments' && identityView === 'users' && (
                    <RoleAssignmentsSection
                      roleAssignments={identityAssignments}
                      onRefresh={handleIdentityRefreshAndUsers}
                      language={currentLanguage}
                    />
                  )}
                  {mainTab === 'assignments' && identityView === 'geo' && (
                    <GeographicAccessSection
                      roleAssignments={identityAssignments}
                      language={currentLanguage}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </main>

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateUser={handleCreateUser}
      />
      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        onUpdateUser={handleUpdateUser}
        user={selectedUser}
      />
      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        onConfirm={handleConfirmDelete}
        user={selectedUser}
      />
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => {
          setShowResetPasswordModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />
      <AssignRoleModal
        isOpen={showAssignRoleModal}
        onClose={() => setShowAssignRoleModal(false)}
        onAssign={handleAssignIdentityRole}
        users={identityUsersRaw}
        roles={identityRolesAssignable}
        countries={identityCountries}
        language={currentLanguage}
      />
    </div>
  );
};

export default AdminUsersManagement;
